import React, { useMemo } from 'react';
import { SingleTabSubViewJson } from '../../../utils/constants/ReportTabsType';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { PLOT_TITLES } from '../../../utils/constants/ReportTabs';
import TitleWithHorizontalDivider from '../../atom/TitleWithHorizontalDivider/TitleWithHorizontalDivider';

/**
 * Formats numbers to always show 1 decimal
 * @param {number | string} value - The value to format
 * @returns {string} - The formatted value
 */
const formatNumber = (value: number | string): string => {
  if (typeof value === 'number') {
    return value.toFixed(1); // Always format to 1 decimal
  }
  return value as string;
};

/**
 * Renders a SubView of type Table
 * @param {React.PropsWithChildren<ISubViewTable>} props
 * @return {JSX.Element | null}
 * @constructor
 */
const SubViewTable: React.FC<ISubViewTable> = (props) => {
  const { data, pythonElementName, targetGroupName } = props;

  // 👇 detect the "file not found" placeholder from useGetPostprocessData
  const isFileNotFound =
      data.data.length > 0 && data.data[0].Type === 'File Not Found';

  const headers = useMemo(() => {
    if (isFileNotFound) return [];
    if (data.data.length === 0) return [];
    return Object.keys(data.data[0]);
  }, [data.data, isFileNotFound]);

  const filteredRows = useMemo(() => {
    if (isFileNotFound) return [];
    return data.data.filter((r) =>
        targetGroupName ? r['Target name'] === targetGroupName : true
    );
  }, [data.data, targetGroupName, isFileNotFound]);

  // ✅ after hooks: decide whether to render
  if (isFileNotFound) {
    return null;
  }

  return (
      <Box>
        {PLOT_TITLES[pythonElementName] &&
        PLOT_TITLES[pythonElementName].title ? (
            <Box mb={1}>
              <TitleWithHorizontalDivider
                  name={PLOT_TITLES[pythonElementName].title as string}
              />
            </Box>
        ) : (
            ''
        )}
        <TableContainer>
          <Table size="small" sx={{ mb: '10px' }}>
            <TableHead
                style={{
                  backgroundColor: '#ffffff',
                  height: '45px',
                  borderBottom: '2px solid #ffffff',
                }}
            >
              <TableRow>
                {headers.map((h, pos) => (
                    <TableCell
                        key={h}
                        sx={{ whiteSpace: 'nowrap' }}
                        align={pos === 0 ? 'left' : 'right'}
                    >
                      <strong>{h}</strong>
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((dataRow, posRow) => (
                  <TableRow
                      key={posRow}
                      sx={{
                        borderTop: '2px solid #f2f2f2',
                        borderBottom: '2px solid #f2f2f2',
                        backgroundColor: '#f2f2f2',
                      }}
                  >
                    {Object.keys(dataRow).map((k, posCol) => (
                        <TableCell
                            key={posCol}
                            sx={{
                              whiteSpace: 'nowrap',
                              backgroundColor:
                                  posRow % 2 === 1 ? 'white' : '#f2f2f2',
                            }}
                            align={posCol === 0 ? 'left' : 'right'}
                        >
                          {formatNumber(dataRow[k])}
                        </TableCell>
                    ))}
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
  );
};

export interface ISubViewTable {
  data: SingleTabSubViewJson;
  pythonElementName: string; // represent the python_element name that identifies uniquely a plot/table among all dashboards - like in element_config.json
  targetGroupName?: string | undefined;
}

export default SubViewTable;